import { useState } from 'react';
import { Button } from '../ui/button';
import { Shield, AlertTriangle, Scale, FileText } from 'lucide-react';

export default function TosModal({ onAccept, onDecline }) {
  const [scrolledToBottom, setScrolledToBottom] = useState(false);

  const handleScroll = (e) => {
    const el = e.target;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 30) {
      setScrolledToBottom(true);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm" data-testid="tos-modal-overlay">
      <div className="w-full max-w-2xl mx-4 bg-[hsl(220,18%,11%)] border border-[hsl(220,16%,18%)] rounded-xl shadow-2xl overflow-hidden" data-testid="tos-modal">
        {/* Header */}
        <div className="px-6 py-5 border-b border-[hsl(220,16%,18%)] bg-gradient-to-r from-[#E8520F]/10 to-transparent">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#E8520F]/15 flex items-center justify-center">
              <Scale className="w-5 h-5 text-[#E8520F]" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white font-['Outfit']">Legal & Financial Disclosure</h2>
              <p className="text-xs text-[hsl(215,16%,56%)]">Please read carefully before proceeding</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div
          className="px-6 py-4 max-h-[50vh] overflow-y-auto text-sm text-[hsl(210,20%,80%)] space-y-4 leading-relaxed"
          onScroll={handleScroll}
          data-testid="tos-content"
        >
          <div className="flex items-start gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
            <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-red-400 text-sm">Risk Warning</p>
              <p className="text-xs text-red-300/80 mt-1">
                Trading cryptocurrencies and digital assets involves substantial risk of loss and is not suitable for every investor. 
                The value of digital assets can be extremely volatile. You should carefully consider whether trading is suitable for 
                you in light of your financial condition, risk tolerance, and investment objectives.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-3 rounded-lg bg-[#D4A017]/10 border border-[#D4A017]/20">
            <Shield className="w-5 h-5 text-[#D4A017] mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-[#D4A017] text-sm">Financial Disclaimer</p>
              <p className="text-xs text-[hsl(210,20%,75%)] mt-1">
                GauriLabs Commander is a trading automation tool. It does not provide financial advice, investment recommendations, 
                or solicitations to buy, sell, or hold any digital asset. Past performance does not guarantee future results. 
                Automated trading systems, including bots, may produce losses as well as gains.
              </p>
            </div>
          </div>

          <div>
            <p className="font-semibold text-white flex items-center gap-2 mb-2">
              <FileText className="w-4 h-4 text-[#00CED1]" /> Terms of Service
            </p>
            <div className="space-y-3 text-xs text-[hsl(210,20%,75%)]">
              <p><strong className="text-white">1. Acceptance.</strong> By using GauriLabs Commander ("the Platform"), you acknowledge that you have read, understood, and agree to be bound by these Terms of Service and all applicable laws and regulations.</p>
              
              <p><strong className="text-white">2. Eligibility.</strong> You must be at least 18 years old and legally permitted to use financial trading software in your jurisdiction. You are solely responsible for ensuring compliance with all local laws.</p>
              
              <p><strong className="text-white">3. No Guaranteed Returns.</strong> The Platform makes no guarantees regarding profits, returns, or performance of any trading strategy. All trading involves risk, including the potential loss of principal.</p>
              
              <p><strong className="text-white">4. Credits & Fees.</strong> Platform usage requires Credits, which are consumed when starting bots and executing trades. Credits are non-refundable once used. Currency-to-Credit conversion rates are subject to change.</p>
              
              <p><strong className="text-white">5. Tiered Execution.</strong> The execution routing (Standard RPC, Dedicated RPC, or Jito Block Engine) depends on your subscription tier. Higher tiers unlock live execution and MEV protection. Strategy tier users operate in Paper Trading mode only.</p>
              
              <p><strong className="text-white">6. Wallet Security.</strong> You are solely responsible for the security of your wallets, private keys, and credentials. GauriLabs is not liable for any loss resulting from unauthorized access to your accounts.</p>
              
              <p><strong className="text-white">7. Data Collection.</strong> We collect and store your acceptance of these terms, including timestamps and IP addresses, for legal compliance purposes.</p>
              
              <p><strong className="text-white">8. Limitation of Liability.</strong> Under no circumstances shall GauriLabs, its officers, directors, employees, or agents be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Platform.</p>
              
              <p><strong className="text-white">9. Modification.</strong> GauriLabs reserves the right to modify these terms at any time. Continued use of the Platform after changes constitutes acceptance of the modified terms.</p>
              
              <p><strong className="text-white">10. Governing Law.</strong> These terms shall be governed by and construed in accordance with the laws of the jurisdiction in which GauriLabs operates.</p>
            </div>
          </div>

          <div className="text-[10px] text-[hsl(215,16%,40%)] pt-2 border-t border-[hsl(220,16%,18%)]">
            Version 1.0 — Last updated: April 2026. By clicking "I Accept", you confirm that you have read, understood, and agree to all terms above.
          </div>
        </div>

        {/* Actions */}
        <div className="px-6 py-4 border-t border-[hsl(220,16%,18%)] flex items-center justify-between bg-[hsl(220,18%,10%)]">
          <p className="text-xs text-[hsl(215,16%,45%)]">
            {scrolledToBottom ? 'You may now accept or decline.' : 'Please scroll to the bottom to continue.'}
          </p>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={onDecline}
              className="border-red-500/30 text-red-400 hover:bg-red-500/10 bg-transparent"
              data-testid="tos-decline-btn"
            >
              Decline & Logout
            </Button>
            <Button
              onClick={onAccept}
              disabled={!scrolledToBottom}
              className="bg-[#E8520F] hover:bg-[#C9440C] text-white disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="tos-accept-btn"
            >
              I Accept
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
